import PageTransition from '../components/PageTransition';
import { useBlog } from '../context/BlogContext';
import SubscribeBox from '../components/SubscribeBox';
import { motion } from 'framer-motion';
import { hasRichContent } from '../utils/richContent';

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 15 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: [0.4, 0, 0.2, 1] },
});

const Section = ({ title, children, delay }) => (
  <motion.div {...fade(delay)} style={{ marginBottom: '3rem' }}>
    <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.4rem', marginBottom: '1rem', textAlign: 'center' }}>{title}</h2>
    {children}
  </motion.div>
);

const P = ({ children }) => (
  <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', lineHeight: 1.85, marginBottom: '1rem', textAlign: 'center' }}>{children}</p>
);

const Bullet = ({ children }) => (
  <li style={{ color: 'var(--text-secondary)', fontSize: '1rem', lineHeight: 2.2, listStyle: 'none', textAlign: 'center' }}>
    <span style={{ color: 'var(--text-tertiary)', marginRight: '0.5rem' }}>-</span>{children}
  </li>
);

const Divider = () => (
  <div style={{ width: '40px', height: '1px', background: 'var(--border-subtle)', margin: '2.5rem auto' }} />
);

export default function Philosophy() {
  const { siteSettings, settingsLoading } = useBlog();
  const hasCustom = hasRichContent(siteSettings?.philosophyContent || '');

  return (
    <PageTransition>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '3rem 2rem 5rem' }}>
        {!settingsLoading && (hasCustom ? (
          <motion.div {...fade(0.2)} className="rich-content">
            <div dangerouslySetInnerHTML={{ __html: siteSettings.philosophyContent }} />
          </motion.div>
        ) : (
          <>
            <motion.div {...fade(0.1)} style={{ marginBottom: '2.5rem' }}>
              <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem, 5vw, 2.5rem)', lineHeight: 1.2, fontWeight: 400, textAlign: 'center' }}>
                Brand Philosophy
              </h1>
            </motion.div>

            <Section title="Who We Are" delay={0.2}>
              <P>UMI is a community built around the quiet, powerful world of mushrooms. We are growers, thinkers, cooks, and curious minds who believe that what grows beneath the surface makes everything else possible.</P>
              <P>We started with a simple question: what if we treated mushrooms not as a trend, but as teachers? That question became a philosophy, and that philosophy became UMI.</P>
            </Section>

            <Divider />

            <Section title="What UMI Means" delay={0.25}>
              <P>UMI comes from <em>umami</em> - depth, savor, fullness - but also from what is <strong>unseen</strong>.</P>
              <P>In forests, the understory is where real life happens. Not in the canopy. Not in spectacle. But underground - in networks, decay, exchange, patience, and time.</P>
              <P>Mushrooms belong there. And so do we.</P>
            </Section>

            <Divider />

            <Section title="How UMI Thinks" delay={0.3}>
              <ul style={{ padding: 0, marginBottom: '1rem' }}>
                <Bullet>Food should be understood before it is optimized</Bullet>
                <Bullet>Process matters more than polish</Bullet>
                <Bullet>Variability is not a flaw, it is a signal of life</Bullet>
                <Bullet>Slowness is a feature, not a failure</Bullet>
                <Bullet>Depth creates loyalty, not volume</Bullet>
              </ul>
              <P>We don't chase scale before sense. We don't simplify what deserves complexity. And we don't rush what needs time.</P>
            </Section>

            <Divider />

            <Section title="The Understory" delay={0.4}>
              <P>Calling ourselves <em>"the mushroom understory"</em> is intentional. It means:</P>
              <ul style={{ padding: 0, marginBottom: '1rem' }}>
                <Bullet>We value what happens before harvest</Bullet>
                <Bullet>We honor the work that doesn't show up on a plate</Bullet>
                <Bullet>We build foundations, not facades</Bullet>
                <Bullet>We grow culture before community, and community before commerce</Bullet>
              </ul>
              <P>UMI is not here to dominate the landscape. It is here to <strong>support it</strong>.</P>
            </Section>

            <Divider />

            <Section title="The Invitation" delay={0.5}>
              <P>UMI is for people who are curious. People who notice texture. People who are comfortable with ambiguity. People who understand that good things take time.</P>
              <P>If you're looking for shortcuts, UMI isn't for you. If you're willing to slow down, pay attention, and rethink how food fits into life -</P>
              <p style={{ fontFamily: 'var(--font-serif)', fontSize: '1.3rem', fontStyle: 'italic', color: 'var(--text-primary)', lineHeight: 1.5, textAlign: 'center' }}>
                Welcome to UMI.
              </p>
            </Section>
          </>
        ))}

        <SubscribeBox />
      </div>
    </PageTransition>
  );
}

